import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Collapse } from "reactstrap";
import "./lightTheme.scss";
import { v4 as uuidv4 } from "uuid";

const generateMessageId = () => {
  return `message-${uuidv4()}`;
};
const importDarktMode = (mainTheme) => {
  if (mainTheme === "dark") {
    require("./darkTheme.scss");
  }
};

const ComponentMessage = ({
  isRight,
  name,
  message,
  msg,
  files,
  time,
  hasPrivateNote = false,
  isPrivate = false,
  isDeleted = false,
  title = "",
}) => {
  const { mainTheme } = useSelector((state) => state.Layout);

  importDarktMode(mainTheme);

  useEffect(() => {
    importDarktMode(mainTheme);
  }, [mainTheme]);

  const [isOpen, setIsOpen] = useState(true);

  function toggle() {
    setIsOpen((prev) => !prev);
  }

  const { t } = useTranslation();

  const renderSingleFile = useCallback(() => {
    if (msg?.by === "consumer") {
      return (
        <p className="file-text">
          <a
            href={files?.file_url}
            target="_blank"
            className="header-name"
            rel="noreferrer"
          >
            <i className="ml-1">
              <div className="file-name" style={{ display: "inline-block" }}>
                {files?.file_name}
              </div>
              {files?.file_size &&
                `(${(files?.file_size / 1000000).toFixed(3)} MB)`}
            </i>
          </a>
        </p>
      );
    }
  }, [files?.file_name, files?.file_size, files?.file_url, msg?.by]);

  const makeLinksClickable = (text) => {
    if (!text) {
      return null;
    }
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const messageId = generateMessageId();
      const parts = line.split(urlRegex);
      return (
        <React.Fragment key={`${messageId}:${lineIndex}`}>
          {parts.map((part, index) => {
            if (urlRegex.test(part)) {
              return (
                <a
                  key={`${messageId}:${lineIndex}:${index}`}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textTransform: "none" }}
                >
                  {part}
                </a>
              );
            }
            return (
              <span
                key={`${messageId}:${lineIndex}:${index}`}
                style={{ textTransform: "none" }}
              >
                {part}
              </span>
            );
          })}
          <br />
        </React.Fragment>
      );
    });
  };

  const titleMessage = useMemo(() => {
    if (msg.by !== "consumer") {
      if (msg?.type_info === "cvcom") {
        if (msg?.is_public) return t("Public comment");

        return t("Private comment");
      }

      if (msg?.type_info === "cvautflow") return title;
      if (hasPrivateNote) return t("Private Note");

      return t("Public Note");
    }

    return "--";
  }, [msg?.type_info, msg.by, t, hasPrivateNote, title]);

  const MessageIcon = useCallback(() => {
    if (msg?.by !== "consumer" && msg?.type_info === "cvcom") {
      return <i className="bx bx-message-detail" />;
    }

    return null;
  }, [msg.by, msg.type_info]);

  return (
    <div
      className={`tabContainer d-flex ${
        isRight ? "justify-content-end" : "consumerLeft"
      }`}
      style={{ clear: "both" }}
    >
      <div className="content-tab">
        <div className="d-flex messageItem__header" onClick={toggle}>
          <div
            className="item-tab"
            style={{ flex: 1, justifyContent: "flex-start" }}
          >
            <MessageIcon />
            <span className={isDeleted && "commentaryRemovedColor"}>
              {titleMessage}
            </span>
            {isDeleted && (
              <div className="d-flex align-items-center justify-content-center px-2 excluded-label">
                <span>{t("Deleted")}</span>
              </div>
            )}
          </div>
          <div
            className="item-tab"
            style={{ flex: 0.4, justifyContent: "flex-end" }}
          >
            <div>{t(isOpen ? "View Less" : "View More")}</div>
            {isOpen ? (
              <i className="bx bx-chevron-up" />
            ) : (
              <i className="bx bx-chevron-down" />
            )}
          </div>
        </div>
        <Collapse isOpen={isOpen}>
          <div className="content-items">
            {(message || files?.length > 0) && (
              <>
                <div className="tab-item mt-2">
                  <div className="header">
                    <div
                      className={`header-name ${
                        isDeleted && "commentaryRemovedColor"
                      }`}
                    >
                      {makeLinksClickable(message) || ""}
                    </div>
                    {files?.length > 0 && (
                      <h4
                        className="mt-3"
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 400,
                          color: "#FFF",
                        }}
                      >
                        {t("Attached files")}
                      </h4>
                    )}

                    <div className="file-container  pl-2">
                      {Array.isArray(files)
                        ? files?.map((file) => {
                            return (
                              <p className="file-text">
                                <a
                                  href={file?.file_url}
                                  target="_blank"
                                  className="header-name"
                                  rel="noreferrer"
                                >
                                  <i className="ml-1">
                                    <div
                                      className="file-name"
                                      style={{ display: "inline-block" }}
                                    >
                                      {file?.file_name}
                                    </div>
                                    {file?.file_size &&
                                      `(${(file?.file_size / 1000000).toFixed(
                                        3
                                      )} MB)`}
                                  </i>
                                </a>
                              </p>
                            );
                          })
                        : renderSingleFile()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Collapse>
      </div>
    </div>
  );
};
export default memo(ComponentMessage);
